import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { managementCompanyApi, roleApi } from '@/services/api/organizationApi';
import { Loader2, Shield, Lock } from 'lucide-react';
import { supabase } from '@/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { associationApi } from '@/services/api';
import { Association } from '@/models/Association';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface RoleAssignmentFormProps {
  userId: number;
  entityType: 'platform' | 'management_company' | 'association';
  entityId?: number;
  onRoleAssigned?: (roleId: number, entityId?: number) => void;
  onRoleRemoved?: (roleId: number, entityId?: number) => void;
  initialRoles?: number[] | Record<string, number[]>;
}

interface EntityRoles {
  [key: string]: number[];
}

export function RoleAssignmentForm({ userId, entityType, onRoleAssigned, onRoleRemoved, initialRoles }: RoleAssignmentFormProps) {
  const [selectedAssociations, setSelectedAssociations] = React.useState<number[]>([]);
  const [entityRoles, setEntityRoles] = React.useState<EntityRoles>({});
  const { selectedRole, hasPermission, getCurrentRoleIds } = useAuth();

  // Check if user has permission to read roles
  const canReadRoles = React.useMemo(() => {
    const currentRoleIds = getCurrentRoleIds();
    return hasPermission(currentRoleIds, 'read', 'roles');
  }, [hasPermission, getCurrentRoleIds]);

  // Check if user has permission to write/modify roles
  const canWriteRoles = React.useMemo(() => {
    const currentRoleIds = getCurrentRoleIds();
    return hasPermission(currentRoleIds, 'write', 'roles');
  }, [hasPermission, getCurrentRoleIds]);

  const canGrantPermission = React.useMemo(() => {
    const currentRoleIds = getCurrentRoleIds();
    return hasPermission(currentRoleIds, 'grant', 'permission');
  }, [hasPermission, getCurrentRoleIds]);

  const { data: roles, isLoading: rolesLoading } = useQuery({
    queryKey: ['roles', selectedRole?.type, selectedRole?.entityId],
    queryFn: () => roleApi.list(),
    enabled: canReadRoles, // Only fetch if user has permission
  });

  const { data: managementCompanies } = useQuery({
    queryKey: ['management-companies', selectedRole?.type, selectedRole?.entityId],
    queryFn: () => managementCompanyApi.list(),
    enabled: entityType === 'management_company'
  });

  const { data: associations } = useQuery({
    queryKey: ['associations', selectedRole?.type, selectedRole?.entityId],
    queryFn: () => associationApi.list(),
    enabled: entityType === 'association' && !!selectedRole
  });

  React.useEffect(() => {
    if (initialRoles) {
      if (entityType === 'platform' && Array.isArray(initialRoles)) {
        setEntityRoles({ platform: initialRoles });
      } else if ((entityType === 'management_company' || entityType === 'association') && typeof initialRoles === 'object' && !Array.isArray(initialRoles)) {
        setEntityRoles(initialRoles);
        
        if (entityType === 'association') {
          const associationIds = Object.keys(initialRoles).map(id => parseInt(id));
          if (associationIds.length > 0) {
            setSelectedAssociations(associationIds);
          }
        }
      }
    }
  }, [initialRoles, entityType]);

  const handleAddAssociationRole = (e: React.MouseEvent) => {
    e.preventDefault();
    setSelectedAssociations(prev => [...prev, -1]);
  };

  const handleAddRole = (entityId: string, roleId: number) => {
    setEntityRoles(prev => ({
      ...prev,
      [entityId]: [...(prev[entityId] || []), roleId]
    }));
    onRoleAssigned?.(roleId, entityId === 'platform' ? undefined : Number(entityId));
  };

  const handleRemoveRole = (entityId: string, roleId: number) => {
    setEntityRoles(prev => ({
      ...prev,
      [entityId]: prev[entityId]?.filter(id => id !== roleId) || []
    }));
    onRoleRemoved?.(roleId, entityId === 'platform' ? undefined : Number(entityId));
  };

  const canGrantAdminAccess = () => {
    const currentRoleIds = getCurrentRoleIds();
    return hasPermission(currentRoleIds, 'grant', 'admin_access');
  };

  const getAvailableRoles = (entityId: string) => {
    const availableRoles = roles?.filter(role => !entityRoles[entityId]?.includes(role.id)) || [];
    // filter out admin roles if you don't have permission to grant admin access
    if(!canGrantAdminAccess()) {
      return availableRoles.filter(role => !role.name.toLowerCase().includes('admin') && !role.name.toLowerCase().includes('administrator'));
    }
    return availableRoles;
  };

  const getAllRoles = (entityId: string) => {
    // Return all roles assigned to this entity (including admin roles)
    return entityRoles[entityId] || [];
  };

  const getRoleName = (roleId: number) => {
    return roles?.find(role => role.id === roleId)?.name || '';
  };

  if (rolesLoading) {
    return <div className="flex justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  }

  // Show fallback UI if user doesn't have permission to read roles
  if (!canReadRoles) {
    return (
      <div className="space-y-4">
        <Alert className="border-amber-200 bg-amber-50">
          <Lock className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span className="font-medium">Access Restricted</span>
            </div>
            <p className="mt-1 text-sm">
              You don't have permission to view or manage roles. Please contact your administrator if you need access to role management features.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (entityType === 'platform') {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-2 border rounded-md">
          {getAllRoles('platform')?.map((roleId) => (
            <Badge key={roleId} variant="secondary" className="gap-1">
              {getRoleName(roleId)}
              {canGrantPermission && (
                <X 
                  className="h-3 w-3 cursor-pointer" 
                  onClick={() => handleRemoveRole('platform', roleId)}
                />
              )}
            </Badge>
          ))}
          {canGrantPermission && getAvailableRoles('platform').length > 0 && (
            <Select onValueChange={(value) => handleAddRole('platform', parseInt(value))}>
              <SelectTrigger className="w-auto border-none bg-transparent p-0 h-6">
                <span className="text-xs text-muted-foreground ml-2">Add role</span>
              </SelectTrigger>
              <SelectContent>
                {getAvailableRoles('platform').map((role) => (
                  <SelectItem key={role.id} value={role.id.toString()}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {entityType === 'association' && canWriteRoles && (
        <Button 
          onClick={handleAddAssociationRole}
          variant="outline"
          size="sm"
          className="mb-4"
          type="button"
        >
          Add role
        </Button>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{entityType === 'management_company' ? 'Company' : 'Association'}</TableHead>
            <TableHead>Roles</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entityType === 'management_company' ? (
            managementCompanies?.map((company) => (
              <TableRow key={company.id}>
                <TableCell>{company.name}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-2 min-h-[2.5rem]">
                    {getAllRoles(company.id.toString())?.map((roleId) => (
                      <Badge key={roleId} variant="secondary" className="gap-1">
                        {getRoleName(roleId)}
                        {canGrantPermission && (
                          <X 
                            className="h-3 w-3 cursor-pointer" 
                            onClick={() => handleRemoveRole(company.id.toString(), roleId)}
                          />
                        )}
                      </Badge>
                    ))}
                    {canGrantPermission && getAvailableRoles(company.id.toString()).length > 0 && (
                      <Select onValueChange={(value) => handleAddRole(company.id.toString(), parseInt(value))}>
                        <SelectTrigger className="w-auto border-none bg-transparent p-0 h-6">
                          <span className="text-xs text-muted-foreground ml-2">Add role</span>
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailableRoles(company.id.toString()).map((role) => (
                            <SelectItem key={role.id} value={role.id.toString()}>
                              {role.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            [...(selectedAssociations)].map((associationId, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Select 
                    value={associationId > 0 ? associationId.toString() : undefined}
                    onValueChange={(value) => {
                      const newAssociations = [...selectedAssociations];
                      newAssociations[index] = parseInt(value);
                      setSelectedAssociations(newAssociations);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select an association" />
                    </SelectTrigger>
                    <SelectContent>
                      {(associations as Association[])?.map((association) => (
                        <SelectItem key={association.id} value={association.id.toString()}>
                          {association.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-2 min-h-[2.5rem]">
                    {associationId > 0 && getAllRoles(associationId.toString())?.map((roleId) => (
                      <Badge key={roleId} variant="secondary" className="gap-1">
                        {getRoleName(roleId)}
                        {canGrantPermission && (
                          <X 
                            className="h-3 w-3 cursor-pointer" 
                            onClick={() => handleRemoveRole(associationId.toString(), roleId)}
                          />
                        )}
                      </Badge>
                    ))}
                    {canGrantPermission && associationId > 0 && getAvailableRoles(associationId.toString()).length > 0 && (
                      <Select onValueChange={(value) => handleAddRole(associationId.toString(), parseInt(value))}>
                        <SelectTrigger className="w-auto border-none bg-transparent p-0 h-6">
                          <span className="text-xs text-muted-foreground ml-2">Add role</span>
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailableRoles(associationId.toString()).map((role) => (
                            <SelectItem key={role.id} value={role.id.toString()}>
                              {role.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
